import React from 'react';
import loadable from '@loadable/component';

import { Box, Inline } from '@styleseat/ui';
import {
  Button,
  ModalBox,
  PortalOverlay,
  PortalOverlayHeader,
  PortalOverlayHeaderCloseBtn,
  PortalOverlayModal,
} from '../../ui';
import SSErrorBoundary from '../../SSErrorBoundary';
import PageLoadError from '../PageLoadError';

const ConsumerTOS = loadable(() => import('./ConsumerTOS'));
const PrivacyPolicy = loadable(() => import('./PrivacyPolicy'));
const ProTOS = loadable(() => import('./ProTOS'));
const EulaAndroid = loadable(() => import('./EulaAndroid'));
const EulaIOS = loadable(() => import('./EulaIOS'));

export enum LegalDoc {
  PrivacyPolicyInfo = 'privacy_policy',
  ConsumerTermsOfService = 'consumer_terms_of_service',
  EulaAndroidInfo = 'eula_android',
  EulaIOSInfo = 'eula_ios',
  ProTermsOfService = 'pro_terms_of_service',
}

type LegalModalProps = {
  doc: LegalDoc;
  onConfirm?: () => void;
  onDismiss?: () => void;
  confirmText?: string;
  dismissText?: string;
  mandatory?: boolean;
};

export const LegalModal: React.FC<LegalModalProps> = ({
  doc,
  onConfirm,
  onDismiss,
  confirmText = 'Accept',
  dismissText = 'Close',
  mandatory = false,
}) => (
  <PortalOverlay
    name="legal-modal"
    onBackdropClick={!mandatory && onDismiss}
    onRequestExternalClose={!mandatory && onDismiss}
  >
    <PortalOverlayModal position="fullScreen" testID={`legal-modal__${doc}`}>
      {!mandatory && (
        <PortalOverlayHeader
          renderRightAction={(() => (
            <PortalOverlayHeaderCloseBtn
              onCloseRequest={onDismiss}
              testID="legal-modal__dismiss"
            />
          ))}
        />
      )}
      <SSErrorBoundary info={`LegalModal ${doc}`} renderFallback={() => <PageLoadError />}>
        <ModalBox scrollable>
          {doc === 'privacy_policy' && <PrivacyPolicy />}
          {doc === 'consumer_terms_of_service' && <ConsumerTOS />}
          {doc === 'pro_terms_of_service' && <ProTOS />}
          {doc === 'eula_ios' && <EulaIOS />}
          {doc === 'eula_android' && <EulaAndroid />}
        </ModalBox>
      </SSErrorBoundary>
      <Inline padding={16} space={16} showBoxShadow>
        <Box flexGrow={1}>
          <Button
            onClick={onDismiss}
            appearance={onConfirm ? 'outline-thin' : 'primary-neue'}
            testID="legal-modal__cancel"
          >
            {dismissText}
          </Button>
        </Box>
        {onConfirm && (
          <Box flexGrow={1}>
            <Button onClick={onConfirm} appearance="primary-neue" testID="legal-modal__confirm">
              {confirmText}
            </Button>
          </Box>
        )}
      </Inline>
    </PortalOverlayModal>
  </PortalOverlay>
);
